import React, { useState } from 'react';
import { CheckCircle, XCircle, RotateCcw, Settings, Brain, FileText, Plus, Minus, Globe, Eye, EyeOff, Wifi } from 'lucide-react';
import comprehensionApiService from '../services/comprehensionApi';
import { comprehensionQuestionTypes, mockComprehensionPassages } from '../data/comprehensionData';
import apiConnectionTest from '../services/testApiConnection';

const Comprehension = () => {
  const [mode, setMode] = useState('setup'); // 'setup', 'quiz', 'results'
  const [passage, setPassage] = useState('');
  const [quizConfig, setQuizConfig] = useState({
    totalQuestions: 10,
    questions: [
      { paragraph: 1, type: 'MC', details: 'Main idea of the paragraph' },
      { paragraph: 2, type: 'FB', details: 'Key terms or phrases' },
      { paragraph: 3, type: 'TF', details: 'Factual statements from the text' },
    ]
  });
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showPassage, setShowPassage] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);

  const addQuestion = () => {
    setQuizConfig(prev => ({
      ...prev,
      questions: [...prev.questions, { 
        paragraph: 1, 
        type: 'MC', 
        details: '' 
      }]
    }));
  };

  const removeQuestion = (index) => {
    setQuizConfig(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateQuestion = (index, field, value) => {
    setQuizConfig(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const testApiConnection = async () => {
    setIsTestingConnection(true);
    setApiStatus(null);
    
    try {
      const result = await apiConnectionTest.testConnection();
      setApiStatus(result);
      
      if (result.success) {
        console.log('API connection successful:', result.response);
      } else {
        console.error('API connection failed:', result.error);
      }
    } catch (error) {
      setApiStatus({
        success: false,
        error: error.message
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const generateQuestions = async () => {
    if (!passage.trim()) {
      alert('Please enter a reading passage first.');
      return;
    }

    setIsGenerating(true);
    try {
      // Try real API first, fallback to mock if API key not configured
      let response;
      try {
        response = await comprehensionApiService.generateComprehensionQuestions(passage, quizConfig);
      } catch (error) {
        console.log('Using mock data due to API error:', error);
        response = await comprehensionApiService.generateMockQuestions(passage, quizConfig);
      }

      if (response.success) {
        setGeneratedQuestions(response.questions);
        setMode('quiz');
        setCurrentQuestion(0);
        setAnswers({});
        setShowResults(false);
      } else {
        alert('Failed to generate questions: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      alert('Error generating questions. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAnswer = (questionIndex, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };

  const checkAnswer = (questionIndex) => {
    const question = generatedQuestions[questionIndex];
    const userAnswer = answers[questionIndex];
    
    if (!question || userAnswer === undefined) return false;
    
    switch (question.type) {
      case 'MC':
        return userAnswer === question.correctAnswer;
      case 'TF':
        return userAnswer === question.correctAnswer;
      case 'FB':
        return userAnswer.toLowerCase().trim() === question.correctAnswer.toLowerCase().trim();
      case 'SQ':
        // For short answer, we'll consider it correct if it's not empty
        return userAnswer && userAnswer.trim().length > 0;
      case 'OR':
        // For ordering questions, check if the order matches
        return JSON.stringify(userAnswer) === JSON.stringify(question.correctOrder);
      default:
        return false;
    }
  };

  const getScore = () => {
    if (generatedQuestions.length === 0) return 0;
    
    let correct = 0;
    for (let i = 0; i < generatedQuestions.length; i++) {
      if (checkAnswer(i)) correct++;
    }
    
    return Math.round((correct / generatedQuestions.length) * 100);
  };

  const renderQuestion = (question, index) => {
    const isCorrect = checkAnswer(index);
    const userAnswer = answers[index];

    return (
      <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          {isCorrect ? (
            <XCircle className="w-6 h-6 text-red-500" />
          ) : (
            <CheckCircle className="w-6 h-6 text-green-500" />
          )}
          <span className="font-medium text-gray-700">
            Question {index + 1} - Paragraph {question.paragraph}
          </span>
        </div>

        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-800 mb-3">{question.question}</h3>
          
          {question.type === 'MC' && (
            <div className="space-y-2">
              {question.options.map((option, optionIndex) => (
                <label key={optionIndex} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name={`question-${index}`}
                    value={optionIndex}
                    checked={userAnswer === optionIndex}
                    onChange={(e) => handleAnswer(index, parseInt(e.target.value))}
                    className="text-blue-600"
                    disabled={showResults}
                  />
                  <span className="text-gray-700">{String.fromCharCode(65 + optionIndex)}) {option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'TF' && (
            <div className="space-y-2">
              {['True', 'False', 'Not Given'].map((option) => (
                <label key={option} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name={`question-${index}`}
                    value={option}
                    checked={userAnswer === option}
                    onChange={(e) => handleAnswer(index, e.target.value)}
                    className="text-blue-600"
                    disabled={showResults}
                  />
                  <span className="text-gray-700">{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'FB' && (
            <div>
              <input
                type="text"
                value={userAnswer || ''}
                onChange={(e) => handleAnswer(index, e.target.value)}
                placeholder="Enter your answer..."
                className="w-full p-2 border border-gray-300 rounded-md"
                disabled={showResults}
              />
            </div>
          )}

          {question.type === 'SQ' && (
            <div>
              <textarea
                value={userAnswer || ''}
                onChange={(e) => handleAnswer(index, e.target.value)}
                placeholder="Enter your answer..."
                className="w-full p-2 border border-gray-300 rounded-md h-20"
                disabled={showResults}
              />
            </div>
          )}

          {question.type === 'OR' && (
            <div>
              <p className="text-sm text-gray-600 mb-3">Enter numbers to indicate the correct order:</p>
              <div className="grid grid-cols-2 gap-4">
                {question.events.map((event, eventIndex) => (
                  <div key={eventIndex} className="flex items-center gap-2">
                    <span className="text-gray-600">#{eventIndex + 1}</span>
                    <span className="text-gray-700">{event}</span>
                    <input
                      type="number"
                      min="1"
                      max={question.events.length}
                      value={userAnswer?.[eventIndex] || ''}
                      onChange={(e) => {
                        const newOrder = [...(userAnswer || [])];
                        newOrder[eventIndex] = parseInt(e.target.value);
                        handleAnswer(index, newOrder);
                      }}
                      className="w-16 p-1 border border-gray-300 rounded text-center"
                      disabled={showResults}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showResults && (
          <div className="bg-blue-50 p-4 rounded-md">
            <h4 className="font-medium text-blue-800 mb-2">Explanation:</h4>
            <p className="text-blue-700">{question.explanation}</p>
          </div>
        )}
      </div>
    );
  };

  const resetQuiz = () => {
    setMode('setup');
    setGeneratedQuestions([]);
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
  };

  const loadSamplePassage = (passageId) => {
    const sample = mockComprehensionPassages.find(p => p.id === passageId);
    if (sample) {
      setPassage(sample.content);
    }
  };

  const getWordCount = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  if (mode === 'quiz') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">AI-Generated English Reading Comprehension Quiz</h1>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-gray-600">
                    Question {currentQuestion + 1} of {generatedQuestions.length}
                  </span>
                  <span className="text-gray-500">
                    Paragraph {generatedQuestions[currentQuestion]?.paragraph} | {generatedQuestions[currentQuestion]?.type}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowPassage(!showPassage)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {showPassage ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showPassage ? 'Hide Passage' : 'Show Passage'}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Reading Passage */}
            {showPassage && (
              <div className="lg:col-span-2">
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-96 overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Reading Passage</h3>
                  <div className="text-gray-700 whitespace-pre-wrap">{passage}</div>
                </div>
              </div>
            )}

            {/* Question */}
            <div className="lg:col-span-1">
              {generatedQuestions.length > 0 && renderQuestion(generatedQuestions[currentQuestion], currentQuestion)}
              
              {/* Navigation */}
              <div className="flex justify-between mt-6">
                <button
                  onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                  disabled={currentQuestion === 0}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                
                {currentQuestion === generatedQuestions.length - 1 ? (
                  <button
                    onClick={() => setShowResults(true)}
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Show Results
                  </button>
                ) : (
                  <button
                    onClick={() => setCurrentQuestion(prev => prev + 1)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Next
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'results') {
    const score = getScore();
    
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Quiz Results</h1>
            <div className="text-6xl font-bold text-red-600 mb-2">{score}%</div>
            <p className="text-gray-600">
              You scored {Math.round((score / 100) * generatedQuestions.length)} out of {generatedQuestions.length} questions correctly.
            </p>
          </div>

          <div className="space-y-6">
            {generatedQuestions.map((question, index) => renderQuestion(question, index))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={resetQuiz}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <RotateCcw className="w-4 h-4 inline mr-2" />
              Take Another Quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Setup mode
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">AI English Reading Comprehension Generator</h1>
              <p className="text-gray-600">Create custom English reading comprehension quizzes with AI-powered question generation</p>
            </div>
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-gray-500" />
              <span className="text-gray-700">English</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Reading Passage Section */}
          <div className="bg-white border-2 border-blue-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Reading Passage</h2>
            </div>
            
            <textarea
              value={passage}
              onChange={(e) => setPassage(e.target.value)}
              placeholder="Paste your English reading passage here... The AI will analyze this text to generate comprehension questions."
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            
            <div className="mt-2 text-sm text-gray-500">
              Word count: {getWordCount(passage)}
            </div>

            {/* Sample Passages */}
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Sample Passages:</h3>
              <div className="flex flex-wrap gap-2">
                {mockComprehensionPassages.map((sample) => (
                  <button
                    key={sample.id}
                    onClick={() => loadSamplePassage(sample.id)}
                    className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200"
                  >
                    {sample.title}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quiz Configuration Section */}
          <div className="bg-white border-2 border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-5 h-5 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-800">Quiz Configuration</h2>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Questions
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={quizConfig.totalQuestions}
                onChange={(e) => setQuizConfig(prev => ({ ...prev, totalQuestions: parseInt(e.target.value) }))}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-800">Question Setup</h3>
                <button
                  onClick={addQuestion}
                  className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
              
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {quizConfig.questions.map((question, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-3 gap-4 mb-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Paragraph</label>
                        <input
                          type="number"
                          min="1"
                          value={question.paragraph}
                          onChange={(e) => updateQuestion(index, 'paragraph', parseInt(e.target.value))}
                          className="w-full p-1 text-sm border border-gray-300 rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                        <select
                          value={question.type}
                          onChange={(e) => updateQuestion(index, 'type', e.target.value)}
                          className="w-full p-1 text-sm border border-gray-300 rounded"
                        >
                          {comprehensionQuestionTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-end">
                        <button
                          onClick={() => removeQuestion(index)}
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Focus/Details</label>
                      <input
                        type="text"
                        value={question.details}
                        onChange={(e) => updateQuestion(index, 'details', e.target.value)}
                        placeholder="e.g., Main idea of the paragraph"
                        className="w-full p-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Question Types Guide */}
        <div className="mt-8 bg-white border-2 border-purple-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Question Types Guide</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {comprehensionQuestionTypes.map(type => (
              <div key={type.value} className="p-3 bg-purple-50 rounded-lg">
                <h3 className="font-medium text-purple-800 mb-1">{type.label}</h3>
                <p className="text-sm text-purple-700">{type.description}</p>
              </div>
            ))}
          </div>
        </div>



        {/* API Connection Test */}
        <div className="text-center mt-6">
          <button
            onClick={testApiConnection}
            disabled={isTestingConnection}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isTestingConnection ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Testing...
              </>
            ) : (
              <>
                <Wifi className="w-4 h-4" />
                Test API Connection
              </>
            )}
          </button>
          
          {apiStatus && (
            <div className={`mt-2 p-2 rounded text-sm ${apiStatus.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {apiStatus.success ? (
                <span>✓ API Connection Successful</span>
              ) : (
                <span>✗ API Connection Failed: {apiStatus.error}</span>
              )}
            </div>
          )}
        </div>

        {/* Generate Quiz Button */}
        <div className="text-center mt-4">
          <button
            onClick={generateQuestions}
            disabled={isGenerating || !passage.trim()}
            className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg text-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Generating...
              </>
            ) : (
              <>
                <Brain className="w-5 h-5" />
                Generate Quiz
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Comprehension; 